/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Action,
  ActionIconDropdown,
  Card,
  Dropdown,
  Fieldset,
  FontWeight,
  HStack,
  ICON_IMAGE_EDIT_OUTLINE,
  Message,
  Portal,
  Spacing,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  useModal,
  useNotifications,
} from '@doveit/bricks';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import useBrandTemplate from '../../hooks/use-brand-template/useBrandTemplate';
import { goToPage } from '../../../utils/navigate/utils';
import {
  BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM, BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM, BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM, CANVA_BRAND_TEMPLATES_URL,
} from '../../constants';
import CanvaModelHCard from '../../components/canva-model-h-card/CanvaModelHCard';
import {
  Agency,
  Agent, Design, PropertyPreview, PropertySearchItem,
} from '../../../providers/api/dtos';
import { MarketingJobStatus } from '../../../domain/types';
import Divider from '../../../components/divider/Divider';
import { createMarketingDesignAutofillJob } from '../../../providers/api/marketing/marketingProvider';
import useMarketingDesignAutofillJob from '../../hooks/use-marketing-design-autofill-job/useMarketingDesignAutofillJob';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import ExportMarketingDesignAction from '../../containers/export-marketing-design-action/ExportMarketingDesignAction';
import { useAutofillData } from '../../hooks/use-autofill-data/useAutofillData';
import { isEmpty } from '../../../utils/object/object';
import {
  decodeBrandTemplateDataset, getReferencedEntitySources,
} from '../../utils/autofill/autofill';
import { toAutofillFailureMessage } from '../../utils/autofill/failure';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { BrandTemplateAutofillAgencyData, BrandTemplateAutofillAgentData, BrandTemplateAutofillPropertyData } from '../../utils/autofill/types';
import TemplatePropertySelector from '../../containers/template-property-selector/TemplatePropertySelector';
import { TemplateAgentSelector } from '../../containers/template-agent-selector/TemplateAgentSelector';
import ViewCanvaDesignPreview from '../../containers/view-canva-design-preview/ViewCanvaDesignPreview';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { TemplateAgencySelector } from '../../containers/template-agency-selector/TemplateAgencySelector';

export const GENERATE_DESIGN_ERROR_MESSAGE = 'Non è stato possibile generare il design con i dati inseriti.';
export const GENERATE_DESIGN_SUCCESS_MESSAGE = 'Il design è stato generato con i dati inseriti.';

export const MARKETING_DESIGN_AUTOFILL_JOB_POLL_INTERVAL_MS = 2_000;

const UseBrandTemplatePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyIdParam = searchParams.get(BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM);
  const agentIdParam = searchParams.get(BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM);
  const agencyIdParam = searchParams.get(BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [pollingJobId, setPollingJobId] = React.useState<string>();
  const { addError } = useNotifications();
  const { data: brandTemplate, isLoading } = useBrandTemplate(id);
  const { userIsMarketing } = useRBAC();
  const [propertyData, setPropertyData] = React.useState<BrandTemplateAutofillPropertyData>();
  const [agentData, setAgentData] = React.useState<BrandTemplateAutofillAgentData>();
  const [agencyData, setAgencyData] = React.useState<BrandTemplateAutofillAgencyData>();
  const isMobile = useIsDevice('mobile');

  React.useEffect(() => {
    if (!brandTemplate) return;

    if (!brandTemplate.dataset || Object.keys(brandTemplate.dataset).length === 0) {
      goToPage(`${CANVA_BRAND_TEMPLATES_URL}/${brandTemplate.id}`);
      navigate('/marketing/canva/brand-templates');
    }
  }, [brandTemplate, navigate]);

  const goToDesignsPage = React.useCallback((designId?: string) => {
    navigate(designId ? `/marketing/canva/designs?design_id=${designId}` : '/marketing/canva/designs');
  }, [navigate]);

  const jobCompletedModal = useModal<Design | undefined>();
  const noFieldsFilledModal = useModal();

  const decodedDataset = React.useMemo(
    () => (brandTemplate?.dataset ? decodeBrandTemplateDataset(brandTemplate.dataset) : undefined),
    [brandTemplate?.dataset],
  );

  const referencedEntities = React.useMemo(
    () => (decodedDataset ? getReferencedEntitySources(decodedDataset.variables) : undefined),
    [decodedDataset],
  );

  const propertyId = propertyIdParam && referencedEntities?.has('property_data') ? Number(propertyIdParam) : undefined;
  const agentId = agentIdParam && referencedEntities?.has('agent_data') ? Number(agentIdParam) : undefined;
  const agencyId = agencyIdParam && referencedEntities?.has('agency_data') ? Number(agencyIdParam) : undefined;

  const {
    data: autofillData,
    isLoading: isAutofillDataLoading,
  } = useAutofillData(decodedDataset?.variables, {
    property_data: propertyData,
    agent_data: agentData,
    agency_data: agencyData,
    // agent_reviews: {}, // @TODO queste andrebbero confezionate dentro agent_data
  });

  const hasUnhandledDesignFields = React.useMemo(() => {
    if (!brandTemplate || isAutofillDataLoading) return false;

    const datasetKeys = Object.keys(brandTemplate.dataset ?? {});
    const resolvedKeys = new Set(Object.keys(autofillData ?? {}));

    return datasetKeys.some((key) => !resolvedKeys.has(key));
  }, [brandTemplate, autofillData, isAutofillDataLoading]);

  const updateSearchParam = React.useCallback((
    key: string,
    value?: string | number,
  ) => {
    setSearchParams((prev) => {
      if (value !== undefined && value !== null) {
        prev.set(key, String(value));
      } else {
        prev.delete(key);
      }

      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const onPropertySelected = React.useCallback((selected: PropertySearchItem | PropertyPreview | null) => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM, selected?.id);
  }, [updateSearchParam]);

  const onAgentSelected = React.useCallback((selected: Agent | null) => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM, selected?.id);
  }, [updateSearchParam]);

  const onAgencySelected = React.useCallback((selected: Agency | null) => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM, selected?.id);
  }, [updateSearchParam]);

  const clearPropertySelection = React.useCallback(() => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM);
  }, [updateSearchParam]);

  const clearAgentSelection = React.useCallback(() => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM);
  }, [updateSearchParam]);

  const clearAgencySelection = React.useCallback(() => {
    updateSearchParam(BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM);
  }, [updateSearchParam]);

  const createAutofillJob = React.useCallback(async () => {
    if (!brandTemplate || !autofillData) return;

    try {
      setIsGenerating(true);

      const autofillJob = await createMarketingDesignAutofillJob({
        brandTemplateId: brandTemplate.id,
        data: autofillData,
      });

      setPollingJobId(autofillJob.id);
    } catch (_) {
      setIsGenerating(false);
      addError(GENERATE_DESIGN_ERROR_MESSAGE);
    }
  }, [addError, autofillData, brandTemplate]);

  const handleProceed = React.useCallback(async () => {
    if (!autofillData || isEmpty(autofillData)) {
      noFieldsFilledModal.open();
      return;
    }

    createAutofillJob();
  }, [autofillData, createAutofillJob, noFieldsFilledModal]);

  useMarketingDesignAutofillJob(pollingJobId, {
    refreshInterval: (data) => {
      if (!data) return MARKETING_DESIGN_AUTOFILL_JOB_POLL_INTERVAL_MS;

      const isTerminal = data.status === MarketingJobStatus.SUCCESS || data.status === MarketingJobStatus.FAILED;
      return isTerminal ? 0 : MARKETING_DESIGN_AUTOFILL_JOB_POLL_INTERVAL_MS;
    },
    onSuccess: (data) => {
      const isTerminal = data.status === MarketingJobStatus.SUCCESS || data.status === MarketingJobStatus.FAILED;

      if (isTerminal) {
        setPollingJobId(undefined);
        setIsGenerating(false);
        jobCompletedModal.open(data.data);
      }
    },
    onError: () => {
      setPollingJobId(undefined);
      setIsGenerating(false);
    },
  });

  return (
    <SimplePageLayout wrapperSize="M">
      {isLoading && (
        <Card>
          <Card.Content>
            <Stack alignItems="center" gap={200}>
              <Spinner size={SpinnerSize.MEDIUM} color="neutrals.medium" />
              <Text.H4 fontWeight={FontWeight.LIGHT}>
                Template in caricamento
              </Text.H4>
            </Stack>
          </Card.Content>
        </Card>
      )}
      {!isLoading && brandTemplate && (
        <Card>
          <Card.Header
            icon={{ path: ICON_IMAGE_EDIT_OUTLINE }}
            title="Seleziona fonti dati"
          />
          <Card.Content>
            <Stack gap={300} wrap="nowrap">
              <Text.BodySmall>Inserisci i dati richiesti e clicca su &quot;Procedi&quot; per generare il design.</Text.BodySmall>
              <Stack>
                <Text.BodySmall fontWeight={FontWeight.REGULAR}>
                  Template scelto
                </Text.BodySmall>

                <CanvaModelHCard brandTemplate={brandTemplate} />
              </Stack>

              {userIsMarketing && decodedDataset && decodedDataset?.severity !== 'none' && (
                <Message
                  type={decodedDataset.severity === 'high' ? 'critical' : 'warning'}
                  boxed
                  message={decodedDataset.severity === 'high' ? 'Il template contiene errori nelle variabili' : 'Il template ha variabili non supportate'}
                >
                  <Stack gap={100}>
                    {decodedDataset.failures.map((f) => {
                      const { variable, description } = toAutofillFailureMessage(f);

                      return (
                        <div key={variable}>
                          La variabile <strong>{variable}</strong> {description}
                        </div>
                      );
                    })}
                  </Stack>
                </Message>
              )}

              {decodedDataset && (
                <Fieldset legend="Dati template">
                  <Stack gap={300} wrap="nowrap">
                    <Message message="La modifica dei dati è finalizzata esclusivamente alla generazione del design." />

                    {referencedEntities?.has('property_data') && (
                      <TemplatePropertySelector
                        propertyId={propertyId}
                        decodedDataset={decodedDataset}
                        onPropertySelected={onPropertySelected}
                        onPropertyDeselected={clearPropertySelection}
                        disabled={isGenerating}
                        onDataChange={setPropertyData}
                      />
                    )}

                    {referencedEntities?.has('agent_data') && (
                      <TemplateAgentSelector
                        agentId={agentId}
                        decodedDataset={decodedDataset}
                        onAgentSelected={onAgentSelected}
                        onAgentDeselected={clearAgentSelection}
                        disabled={isGenerating}
                        onDataChange={setAgentData}
                      />
                    )}

                    {referencedEntities?.has('agency_data') && (
                      <TemplateAgencySelector
                        agencyId={agencyId}
                        decodedDataset={decodedDataset}
                        onAgencySelected={onAgencySelected}
                        onAgencyDeselected={clearAgencySelection}
                        disabled={isGenerating}
                        onDataChange={setAgencyData}
                      />
                    )}
                  </Stack>
                </Fieldset>
              )}
            </Stack>

            <Divider />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
            >
              <Action
                label="Procedi"
                aria-label="Genera design"
                color="primary"
                emphasis="high"
                onClick={handleProceed}
                disabled={isGenerating || isAutofillDataLoading}
                loading={isGenerating || isAutofillDataLoading}
              />
            </div>
          </Card.Content>
        </Card>
      )}
      <Portal>
        <SimpleModal
          {...noFieldsFilledModal}
          title="Dati non inseriti"
          aria-label="Modale dati non inseriti"
          footer={(
            <HStack>
              <Action
                label="Inserisci dati"
                aria-label="Chiudi la modale e inserisci i dati"
                emphasis="high"
                onClick={noFieldsFilledModal.close}
              />
              <Action
                label="Usa template su Canva"
                aria-label="Azione per usare il template su Canva"
                emphasis="high"
                onClick={() => goToPage(`${CANVA_BRAND_TEMPLATES_URL}/${brandTemplate?.id}`)}
              />
            </HStack>
          )}
        >
          Non hai inserito i dati per compilare il design. Inserisci i dati o usa il template su Canva.
        </SimpleModal>

        {jobCompletedModal.data && (
          <SimpleModal
            {...jobCompletedModal}
            close={() => jobCompletedModal.close()}
            title="Design creato con i dati inseriti"
            aria-label="Modale design creato con i dati inseriti"
            footer={(
              <HStack>
                {!hasUnhandledDesignFields && (
                  <ExportMarketingDesignAction
                    size="M"
                    emphasis="high"
                    design={jobCompletedModal.data}
                  />
                )}

                {isMobile && hasUnhandledDesignFields && (
                  <Action
                    aria-label="Azione per modificare design su Canva"
                    onClick={() => goToPage(jobCompletedModal.data!.editUrl)}
                    label="Completa su Canva"
                    size="M"
                    emphasis="high"
                  />
                )}

                {isMobile && (
                  <ActionIconDropdown
                    label="Altro"
                    aria-label="Altre azioni design"
                    emphasis="low"
                  >
                    {!hasUnhandledDesignFields && (
                      <Dropdown.Option
                        aria-label="Azione per modificare design su Canva"
                        label="Modifica su Canva"
                        onClick={() => goToPage(jobCompletedModal.data!.editUrl)}
                      />
                    )}
                    <Dropdown.Option
                      aria-label="Azione per andare alla pagina dei design"
                      label="Vai ai tuoi design"
                      onClick={() => goToDesignsPage(jobCompletedModal.data?.id)}
                    />
                  </ActionIconDropdown>
                )}

                {!isMobile && (
                  <>
                    <Action
                      aria-label="Azione per modificare design su Canva"
                      onClick={() => goToPage(jobCompletedModal.data!.editUrl)}
                      label={hasUnhandledDesignFields ? 'Completa su Canva' : 'Modifica su Canva'}
                      size="M"
                      emphasis={hasUnhandledDesignFields ? 'high' : 'low'}
                    />
                    <Action
                      aria-label="Azione per andare alla pagina dei design"
                      onClick={() => { goToDesignsPage(jobCompletedModal.data?.id); }}
                      label="Vai ai tuoi design"
                      size="M"
                      emphasis="low"
                    />
                  </>
                )}
              </HStack>
            )}
          >
            {hasUnhandledDesignFields
              ? 'Il design è stato creato con i dati che hai inserito. Completalo su Canva.'
              : ` Il design è stato generato con i dati che hai inserito e verrà mostrato nella sezione "I tuoi design Canva".
                  Continua a modificarlo su Canva o scaricalo nel formato più adatto.`}

            <Spacing margin={[300, 0, 0]}>
              <ViewCanvaDesignPreview
                designId={jobCompletedModal.data.id}
              />
            </Spacing>
          </SimpleModal>
        )}
      </Portal>
    </SimplePageLayout>
  );
};

export default UseBrandTemplatePage;
